import type { Channel, FreeformElement, Frame, Item, Mix, PowerSource } from '../model/schema.js';

/** Review groups of the AI import preview (decision U9/U10 step 4). */
export const AI_OP_GROUPS = [
  'stage',
  'channels-new',
  'channels-linked',
  'musicians',
  'instruments',
  'mics',
  'stands',
  'dis',
  'monitors',
  'mixes',
  'stageboxes',
  'power',
  'risers',
  'notes',
  'conflicts',
] as const;
export type AiOpGroup = (typeof AI_OP_GROUPS)[number];

interface OpBase {
  /**
   * Deterministic key derived from the extraction content (not from random ids), so the user's
   * per-row decisions survive re-planning (stored in the import session).
   */
  key: string;
  group: AiOpGroup;
  kind: 'create' | 'patch';
  /** Short human label for the review row, e.g. "Ch 13 Piano Hole" or "Wedge (Drums)". */
  label: string;
  /** Extra detail for the row (model, stand, position …). */
  detail?: string;
  /** Pre-selected in the review (conflict overwrites and stage resizes on busy boards are not). */
  defaultSelected: boolean;
}

export type AiMergeOp =
  | (OpBase & { collection: 'items'; entity: Item })
  | (OpBase & { collection: 'channels'; entity: Channel })
  | (OpBase & { collection: 'mixes'; entity: Mix })
  | (OpBase & { collection: 'power'; entity: PowerSource })
  | (OpBase & { collection: 'freeform'; entity: FreeformElement })
  | (OpBase & { collection: 'frames'; entity: Frame });

export type AiMergeCollection = AiMergeOp['collection'];

/** An extraction entry that matched something already on the board. */
export interface AiMatch {
  kind: 'channel' | 'musician' | 'microphone' | 'di' | 'item' | 'mix' | 'power' | 'note';
  /** Key of the extraction entry (same scheme as op keys). */
  key: string;
  entityId: string;
  label: string;
}

/** Differing non-empty values: the board keeps its value unless the overwrite op is selected. */
export interface AiConflict {
  key: string;
  entityId: string;
  label: string;
  field: string;
  existing: string;
  proposed: string;
  /** Key of the (not pre-selected) overwrite op. */
  opKey: string;
}

export interface AiMergeSummary {
  creates: number;
  patches: number;
  matches: number;
  conflicts: number;
  byGroup: Partial<Record<AiOpGroup, number>>;
}

export interface AiMergePlan {
  ops: AiMergeOp[];
  matches: AiMatch[];
  conflicts: AiConflict[];
  summary: AiMergeSummary;
  /** Warnings from the extraction plus merge warnings (duplicate channel numbers …). */
  warnings: string[];
}

export interface AiMergeOptions {
  newId?: () => string;
}
